import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSnackbar } from 'contexts/SnackbarProvider';
import Loader from 'parts/Loader';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { getErrorMessage } from 'services/ApiService';
import ClientService from 'services/ClientService';
import DynamicTradeService from 'services/DynamicTradeService';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

// Field labels per Dynamic TP/SL rule type — mirrors the reference prototype's FIELD_META:
// one generic two-field row schema (triggerA / atrMult) whose labels change with the selected type.
const DYNAMIC_TP_RULE_TYPES = [
    { value: 'DYNAMIC', label: 'Dynamic (ATR)', aLabel: null, bLabel: 'ATR Multiple' },
    { value: 'LADDER', label: 'Ladder', aLabel: 'Step Size ROI %', bLabel: null },
    { value: 'INVENTORY', label: 'Inventory', aLabel: 'Position Size ($)', bLabel: 'Min ROI % Required' },
    { value: 'INVENTORY_SKEW', label: 'Inventory Skew', aLabel: 'Size Tier ($)', bLabel: null },
    { value: 'USD', label: 'Flat $ Target', aLabel: 'Profit Target ($)', bLabel: null },
];

const DYNAMIC_SL_RULE_TYPES = [
    { value: 'BREAK_EVEN', label: 'Break Even', aLabel: 'Arm At ROI %', bLabel: 'Lock Offset %' },
    { value: 'ATR_TRAIL', label: 'ATR Trail', aLabel: 'Activate At ROI %', bLabel: 'ATR Trail Multiple' },
    { value: 'INVENTORY', label: 'Inventory', aLabel: 'Position Size ($)', bLabel: 'Max Loss ROI % (neg)' },
    { value: 'USD', label: 'Flat $ Limit', aLabel: 'Loss Limit ($, neg)', bLabel: null },
];

// Shared column widths/styles for the Dynamic TP/SL rule tables — keeps both tables' headers
// from wrapping (Field A / Field B) and the Rule Type Select compact.
const RULE_COL_WIDTHS = { type: '125px', field: '110px', exit: '64px', del: '32px' };
const RULE_HEADER_CELL_SX = { fontSize: '11px', padding: '5px 6px', color: '#848e9c', whiteSpace: 'nowrap' };
const RULE_BODY_CELL_SX = { padding: '4px 6px' };
const COMPACT_SELECT_SX = { fontSize: '12px', '& .MuiSelect-select': { padding: '6px 8px' } };
const COMPACT_MENU_ITEM_SX = { fontSize: '12px' };

const atrIntervalOptions = [
    { dataValue: '1m', displayValue: '1m' },
    { dataValue: '5m', displayValue: '5m' },
    { dataValue: '15m', displayValue: '15m' },
    { dataValue: '1h', displayValue: '1h' },
];

const DynamicTradeManager = () => {
    const { control, handleSubmit, reset, setValue, getValues, formState: { errors }, watch, clearErrors } = useForm({
        defaultValues: {
            id: '',
            tradeName: '',
            clientType: 'CLIENT',
            clientId: '',
            clientGroupId: '',
            position: '',
            orderType: 'MARKET',
            spread: '',
            takeProfitEnabled: false,
            takeProfitPercent: '',
            stopLossEnabled: false,
            stopLossPercent: '',
            scaleEnabled: false,
            scale: {
                scalingInEnabled: false,
                scaleIn: {
                    triggerPnl: '',
                    quantity: '',
                    minMaxUsdt: ''
                },
                scalingOutEnabled: false,
                scaleOut: {
                    triggerPnl: '',
                    quantity: '',
                    minMaxUsdt: ''
                }
            },
            trailingStopLossEnabled: false,
            tslLevels: [
                { triggerPnl: '', moveTo: '' },
                { triggerPnl: '', moveTo: '' },
                { triggerPnl: '', moveTo: '' },
            ],
            partialTakeProfitEnabled: false,
            ptpLevels: [
                { targetTp: '', exitQuantity: '' },
                { targetTp: '', exitQuantity: '' },
                { targetTp: '', exitQuantity: '' },
            ],
            finalTpExitPercent: '',
            dynamicTakeProfitEnabled: false,
            dynamicTpRules: [],
            dynamicStopLossEnabled: false,
            dynamicHardStopPercent: '',
            dynamicSlRules: [],
            atrInterval: '5m',
            atrPeriod: 14,
            atrRefreshSeconds: 60,
            autoLoadAllCoinsEnabled: false,
            includeCoins: [],
            excludeCoins: [],
            hedge: {
                enabled: false,
                profitHedgeEnabled: false,
                lossHedgeEnabled: false,
                direction: 'LONG_SHORT',
                type: 'SAME_ASSET',
                tiers: [
                    { drawdownPct: -0.05, allocationPct: 30, tierType: 'LOSS' },
                    { drawdownPct: -0.15, allocationPct: 60, tierType: 'LOSS' },
                    { drawdownPct: -0.30, allocationPct: 100, tierType: 'LOSS' },
                    { drawdownPct: 0.05, allocationPct: 30, tierType: 'PROFIT' },
                    { drawdownPct: 0.15, allocationPct: 60, tierType: 'PROFIT' },
                    { drawdownPct: 0.30, allocationPct: 100, tierType: 'PROFIT' },
                ],
                maxHedgeCapPct: 95,
                unwindEnabled: false,
                unwindBufferPct: 0.05,
                harvestTriggerPct: 0.4,
                harvestMainTriggerPct: null,
                feeFactor: 0.0005,
                minNotionalUsd: 5.5,
                ttlHours: 72,
                takeProfitPct: null,
                safetyExitPct: null,
                profitFrom: '',
                profitTo: '',
                profitAllocationPct: '',
                lossFrom: '',
                lossTo: '',
                lossAllocationPct: '',
                allPositionCoins: true,
                includeSymbols: [],
                crossAssetMap: '',
                baseSymbol: 'ALL',
                crossSymbol: 'ADD'
            },
            status: 'ACTIVE',
            levelLockEnabled: false,
            levelLockConfigs: [
                { trigger: '', add: '', tsl: '', cap: '', lockTarget: '', lockFloor: '' },
            ]
        }
    });

    const { fields: trailingStopLossLevelFields } = useFieldArray({
        control,
        name: 'tslLevels',
    });

    const { fields: partialTakeProfitFields } = useFieldArray({
        control,
        name: 'ptpLevels',
    });

    const { fields: levelLockConfigFields, remove: removeLevelLock, insert: insertLevelLock } = useFieldArray({
        control,
        name: 'levelLockConfigs',
    });

    const { fields: dynamicTpRuleFields, append: appendDynamicTpRule, remove: removeDynamicTpRule } = useFieldArray({
        control,
        name: 'dynamicTpRules',
    });

    const { fields: dynamicSlRuleFields, append: appendDynamicSlRule, remove: removeDynamicSlRule } = useFieldArray({
        control,
        name: 'dynamicSlRules',
    });

    const { fields: hedgeTierFields, append: appendHedgeTier, remove: removeHedgeTier } = useFieldArray({
        control,
        name: 'hedge.tiers',
    });

    const orderTypes = [
        { dataValue: 'MARKET', displayValue: 'MARKET' },
        { dataValue: 'LIMIT', displayValue: 'LIMIT' }
    ]

    const statusTypes = [
        { dataValue: 'ACTIVE', displayValue: 'ACTIVE' },
        { dataValue: 'INACTIVE', displayValue: 'INACTIVE' }
    ]

    const directionTypes = [
        { dataValue: 'LONG', displayValue: 'Long' },
        { dataValue: 'SHORT', displayValue: 'Short' },
        { dataValue: 'LONG_SHORT', displayValue: 'Both' }
    ]

    const baseSymbolList = [
        { dataValue: 'ALL', displayValue: 'All Position Coins' },
        { dataValue: 'ADD', displayValue: 'Add Coin' },
    ]

    const hedgeTypes = [
        { dataValue: 'SAME_ASSET', displayValue: 'Same Asset' },
        { dataValue: 'CROSS_ASSET', displayValue: 'Cross Asset' },
    ]

    const crossSymbolList = [
        { dataValue: 'ADD', displayValue: 'Add Coin' },
    ]

    const defaultHedge = {
        enabled: false,
        profitHedgeEnabled: false,
        lossHedgeEnabled: false,
        direction: "LONG_SHORT",
        type: "SAME_ASSET",
        tiers: [
            { drawdownPct: -0.05, allocationPct: 30, tierType: 'LOSS' },
            { drawdownPct: -0.15, allocationPct: 60, tierType: 'LOSS' },
            { drawdownPct: -0.30, allocationPct: 100, tierType: 'LOSS' },
            { drawdownPct: 0.05, allocationPct: 30, tierType: 'PROFIT' },
            { drawdownPct: 0.15, allocationPct: 60, tierType: 'PROFIT' },
            { drawdownPct: 0.30, allocationPct: 100, tierType: 'PROFIT' },
        ],
        maxHedgeCapPct: 95,
        unwindEnabled: false,
        unwindBufferPct: 0.05,
        harvestTriggerPct: 0.4,
        harvestMainTriggerPct: null,
        feeFactor: 0.0005,
        minNotionalUsd: 5.5,
        ttlHours: 72,
        takeProfitPct: null,
        safetyExitPct: null,
        profitFrom: "",
        profitTo: "",
        profitAllocationPct: "",
        lossFrom: "",
        lossTo: "",
        lossAllocationPct: "",
        allPositionCoins: true,
        includeSymbols: [],
        crossAssetMap: "",
        baseSymbol: "ALL",
        crossSymbol: "ADD"
    };

    const [isLoading, setIsLoading] = useState(false);
    const [clientList, setClientList] = useState([]);
    const [clientGroupList, setClientGroupList] = useState([]);
    const [clientGroupListLoaded, setClientGroupListLoaded] = useState(false);
    const watchClientType = watch("clientType") || 'CLIENT';
    const watchOrderType = watch("orderType");
    const watchTakeProfitEnabled = watch("takeProfitEnabled");
    const watchStopLossEnabled = watch("stopLossEnabled");
    const watchScaleEnabled = watch("scaleEnabled");
    const watchScaleInEnabled = watch("scale.scalingInEnabled");
    const watchScaleOutEnabled = watch("scale.scalingOutEnabled");
    const watchTslEnabled = watch("trailingStopLossEnabled");
    const watchPtpEnabled = watch("partialTakeProfitEnabled");
    const watchDynamicTpEnabled = watch("dynamicTakeProfitEnabled");
    const watchDynamicSlEnabled = watch("dynamicStopLossEnabled");
    const watchDynamicTpRules = watch("dynamicTpRules");
    const watchDynamicSlRules = watch("dynamicSlRules");
    const watchAutoLoadAllCoinsEnabled = watch("autoLoadAllCoinsEnabled");
    const watchBaseSymbols = watch("hedge.baseSymbol");
    const watchHedgeType = watch("hedge.type");
    const watchHedgeEnabled = watch("hedge.enabled");
    const watchProfitHedgeEnabled = watch("hedge.profitHedgeEnabled");
    const watchLossHedgeEnabled = watch("hedge.lossHedgeEnabled");
    const watchHedgeUnwindEnabled = watch("hedge.unwindEnabled");
    const watchHedgeTiers = watch("hedge.tiers");
    const hasIncompleteLossTier = hedgeTierFields.some((f, i) => {
        if (f.tierType !== 'LOSS') return false;
        const live = watchHedgeTiers?.[i];
        return !live?.drawdownPct?.toString().trim() || !live?.allocationPct?.toString().trim();
    });
    const hasIncompleteProfitTier = hedgeTierFields.some((f, i) => {
        if (f.tierType !== 'PROFIT') return false;
        const live = watchHedgeTiers?.[i];
        return !live?.drawdownPct?.toString().trim() || !live?.allocationPct?.toString().trim();
    });
    const watchLevelLockEnabled = watch("levelLockEnabled");
    const { showError, showMessage } = useSnackbar();
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const getClientListAndDynamicTrade = async () => {
            try {
                setIsLoading(true);

                const [clientResponse, dynamicTradeResponse] = await Promise.all([
                    ClientService.getClientList(),
                    DynamicTradeService.getDynamicTradeByTradeId(id),
                ]);

                // handle client response
                const clients = clientResponse?.data ?? [];
                setClientList(clients);

                // handle dynamic trade response
                const dynamicTrade = dynamicTradeResponse?.data;

                if (dynamicTrade) {
                    const clientIds = dynamicTrade.clientIds || [];
                    // A multi-client trade always needs the Group view — even without a stored
                    // clientGroupId (legacy/edge case) — otherwise the other clients would be
                    // silently dropped if the form is saved back as a single-client selection.
                    const initialClientType = (dynamicTrade.clientGroupId || clientIds.length > 1) ? 'CLIENT_GROUP' : 'CLIENT';
                    reset({
                        ...dynamicTrade,
                        clientType: initialClientType,
                        clientId: initialClientType === 'CLIENT' ? (clientIds[0] || '') : '',
                        clientGroupId: dynamicTrade.clientGroupId || '',
                        tslLevels: padArray(dynamicTrade.tslLevels || [], 3, { triggerPnl: '', moveTo: '' }),
                        ptpLevels: padArray(dynamicTrade.ptpLevels || [], 3, { targetTp: '', exitQuantity: '' }),
                        levelLockConfigs: padArray(dynamicTrade.levelLockConfigs || [], 1, { trigger: '', add: '', tsl: '', cap: '', lockTarget: '', lockFloor: '' }),
                        dynamicTakeProfitEnabled: dynamicTrade.dynamicTakeProfitEnabled ?? false,
                        dynamicTpRules: dynamicTrade.dynamicTpRules || [],
                        dynamicStopLossEnabled: dynamicTrade.dynamicStopLossEnabled ?? false,
                        dynamicHardStopPercent: dynamicTrade.dynamicHardStopPercent ?? '',
                        dynamicSlRules: dynamicTrade.dynamicSlRules || [],
                        atrInterval: dynamicTrade.atrInterval || '5m',
                        atrPeriod: dynamicTrade.atrPeriod ?? 14,
                        atrRefreshSeconds: dynamicTrade.atrRefreshSeconds ?? 60,
                        includeCoinValues: dynamicTrade.includeCoins.join('\n'),
                        excludeCoinValues: dynamicTrade.excludeCoins.join('\n'),
                        hedge: {
                            ...defaultHedge,
                            ...(dynamicTrade.hedge ?? {}),
                            direction: 'LONG_SHORT',
                            // null-coalesce new v9.3/v9.6 sub-flags
                            profitHedgeEnabled: dynamicTrade.hedge?.profitHedgeEnabled ?? false,
                            lossHedgeEnabled: dynamicTrade.hedge?.lossHedgeEnabled ?? false,
                            // null-coalesce new v9.6 fields: API returns null for records created before migration
                            tiers: (dynamicTrade.hedge?.tiers?.length > 0)
                                ? dynamicTrade.hedge.tiers.map(t => ({ ...t, tierType: t.drawdownPct < 0 ? 'LOSS' : 'PROFIT' }))
                                : defaultHedge.tiers,
                            maxHedgeCapPct: dynamicTrade.hedge?.maxHedgeCapPct ?? defaultHedge.maxHedgeCapPct,
                            unwindEnabled: dynamicTrade.hedge?.unwindEnabled ?? defaultHedge.unwindEnabled,
                            unwindBufferPct: dynamicTrade.hedge?.unwindBufferPct ?? defaultHedge.unwindBufferPct,
                            harvestTriggerPct: dynamicTrade.hedge?.harvestTriggerPct ?? defaultHedge.harvestTriggerPct,
                            harvestMainTriggerPct: dynamicTrade.hedge?.harvestMainTriggerPct ?? defaultHedge.harvestMainTriggerPct,
                            feeFactor: dynamicTrade.hedge?.feeFactor ?? defaultHedge.feeFactor,
                            minNotionalUsd: dynamicTrade.hedge?.minNotionalUsd ?? defaultHedge.minNotionalUsd,
                            ttlHours: dynamicTrade.hedge?.ttlHours ?? defaultHedge.ttlHours,
                            // v9.3 new fields: nullable
                            takeProfitPct: dynamicTrade.hedge?.takeProfitPct ?? null,
                            safetyExitPct: dynamicTrade.hedge?.safetyExitPct ?? null,
                            // legacy fields: ensure empty string not null
                            profitFrom: dynamicTrade.hedge?.profitFrom ?? '',
                            profitTo: dynamicTrade.hedge?.profitTo ?? '',
                            profitAllocationPct: dynamicTrade.hedge?.profitAllocationPct ?? '',
                            lossFrom: dynamicTrade.hedge?.lossFrom ?? '',
                            lossTo: dynamicTrade.hedge?.lossTo ?? '',
                            lossAllocationPct: dynamicTrade.hedge?.lossAllocationPct ?? '',
                            // sync UI-only baseSymbol from allPositionCoins
                            baseSymbol: (dynamicTrade.hedge?.allPositionCoins === false) ? 'ADD' : 'ALL',
                        },
                    });
                }
            } catch (error) {
                console.error("Error fetching clients list:", error);
                setClientList([]);
            } finally {
                setIsLoading(false);
            }
        }

        const getClientList = async () => {
            try {
                setIsLoading(true);
                const response = await ClientService.getClientList();
                const clients = response?.data ?? [];
                setClientList(clients);
            } catch (error) {
                console.error("Error fetching clients list:", error);
                setClientList([]);
            } finally {
                setIsLoading(false);
            }
        }

        if (id) {
            getClientListAndDynamicTrade();
        } else {
            getClientList();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Group list is only needed when the user actually picks "Group" — avoids an
    // unconditional extra request for the common single-client case.
    useEffect(() => {
        if (watchClientType !== 'CLIENT_GROUP' || clientGroupListLoaded) return;

        const getClientGroupList = async () => {
            try {
                const response = await ClientService.getClientGroupList();
                setClientGroupList(response?.data ?? []);
            } catch (error) {
                console.error("Error fetching client group list:", error);
                setClientGroupList([]);
            } finally {
                setClientGroupListLoaded(true);
            }
        };

        getClientGroupList();
    }, [watchClientType, clientGroupListLoaded]);

    const getGroupValues = (group) => {
        const raw = group?.values
            ?? group?.clientIds
            ?? (Array.isArray(group?.clients) ? group.clients.map(c => c?.id ?? c) : []);
        return Array.isArray(raw) ? raw.map(v => String(v)) : [];
    };

    const sameSet = (left, right) => {
        const a = [...new Set((left || []).map(v => String(v).trim()).filter(Boolean))].sort();
        const b = [...new Set((right || []).map(v => String(v).trim()).filter(Boolean))].sort();
        return a.length === b.length && a.every((value, index) => value === b[index]);
    };

    // Defensive fallback for edit: a multi-client trade with no stored clientGroupId
    // (legacy/edge case) — once the group list loads, try to match its clientIds back
    // to a known group by member set, so the correct group pre-selects automatically.
    useEffect(() => {
        if (!id || !clientGroupListLoaded || clientGroupList.length === 0) return;
        if (getValues('clientGroupId')) return;
        const currentClientIds = getValues('clientIds');
        if (!Array.isArray(currentClientIds) || currentClientIds.length <= 1) return;

        const matched = clientGroupList.find(g => sameSet(getGroupValues(g), currentClientIds));
        if (matched) setValue('clientGroupId', matched.id);
    }, [id, clientGroupListLoaded, clientGroupList]); // eslint-disable-line react-hooks/exhaustive-deps

    const clientMenuItems = useMemo(() => clientList.map((client) => (
        <MenuItem key={client.id} value={client.id}>
            {client.name}
        </MenuItem>
    )), [clientList]);

    const clientGroupMenuItems = useMemo(() => clientGroupList.map((group) => (
        <MenuItem key={group.id} value={group.id}>
            {group.name}
        </MenuItem>
    )), [clientGroupList]);

    useEffect(() => {
        const subscription = watch((values, { name, type }) => {
            if (name === "scale.scalingInEnabled" && !values.scale?.scalingInEnabled) {
                clearErrors([
                    "scale.scaleIn.triggerPnl",
                    "scale.scaleIn.quantity",
                    "scale.scaleIn.minMaxUsdt"
                ]);
            }

            if (name === "scale.scalingOutEnabled" && !values.scale?.scalingOutEnabled) {
                clearErrors([
                    "scale.scaleOut.triggerPnl",
                    "scale.scaleOut.quantity",
                    "scale.scaleOut.minMaxUsdt"
                ]);
            }

            if (name === "scaleEnabled" && !values.scaleEnabled) {
                setValue("scale.scalingInEnabled", false);
                setValue("scale.scalingOutEnabled", false);
            }

            if (name?.startsWith('tslLevels')) {
                const tslLevels = values.tslLevels || [];

                tslLevels.forEach((level, index) => {
                    const isTriggerPnlEmpty = !level.triggerPnl?.toString().trim();
                    const isMoveToEmpty = !level.moveTo?.toString().trim();

                    if (isTriggerPnlEmpty && isMoveToEmpty) {
                        clearErrors([`tslLevels.${index}.triggerPnl`, `tslLevels.${index}.moveTo`]);
                    }
                });
            }

            if (name?.startsWith('ptpLevels')) {
                const ptpLevels = values.ptpLevels || [];

                ptpLevels.forEach((level, index) => {
                    const isTargetTpEmpty = !level.targetTp?.toString().trim();
                    const isExitQuantityEmpty = !level.exitQuantity?.toString().trim();

                    if (isTargetTpEmpty && isExitQuantityEmpty) {
                        clearErrors([`ptpLevels.${index}.targetTp`, `ptpLevels.${index}.exitQuantity`]);
                    }
                });
            }

            if (name?.startsWith('autoLoadAllCoinsEnabled')) {
                if (values.autoLoadAllCoinsEnabled) {
                    setValue("includeCoinValues", "");
                }
            }

            // Dynamic TP/SL is exclusive of Simple TP/SL on the same side (not globally) — enabling
            // one side's Dynamic mode disables that side's Simple mechanisms, and vice versa. The
            // backend enforces this too (supports() guards on the Simple strategies), so this is a
            // UX convenience, not the sole safeguard — learned from the Level Lock exclusivity below,
            // which used to be UI-only.
            if (name === "dynamicTakeProfitEnabled" && values.dynamicTakeProfitEnabled) {
                setValue("takeProfitEnabled", false);
                setValue("partialTakeProfitEnabled", false);
            }
            if ((name === "takeProfitEnabled" && values.takeProfitEnabled)
                || (name === "partialTakeProfitEnabled" && values.partialTakeProfitEnabled)) {
                setValue("dynamicTakeProfitEnabled", false);
            }
            if (name === "dynamicStopLossEnabled" && values.dynamicStopLossEnabled) {
                setValue("stopLossEnabled", false);
                setValue("trailingStopLossEnabled", false);
            }
            if ((name === "stopLossEnabled" && values.stopLossEnabled)
                || (name === "trailingStopLossEnabled" && values.trailingStopLossEnabled)) {
                setValue("dynamicStopLossEnabled", false);
            }

            // Temporary: allow Ratchet to coexist with other strategies
            // if (name === "levelLockEnabled" && values.levelLockEnabled) {
            //     setValue("takeProfitEnabled", false);
            //     setValue("stopLossEnabled", false);
            //     setValue("scaleEnabled", false);
            //     setValue("scale.scalingInEnabled", false);
            //     setValue("scale.scalingOutEnabled", false);
            //     setValue("trailingStopLossEnabled", false);
            //     setValue("partialTakeProfitEnabled", false);
            // }
        });

        return () => subscription.unsubscribe();
    }, [watch, clearErrors, setValue]);

    const padArray = (arr, length, fill) => {
        const result = [...arr];
        while (result.length < length) {
            result.push({ ...fill });
        }
        return result;
    };

    const validateTslRow = (triggerPnl, moveTo) => {
        if (watchTslEnabled) {
            const isTriggerPnlEmpty = !triggerPnl?.toString().trim();
            const isMoveToEmpty = !moveTo?.toString().trim();

            if (isTriggerPnlEmpty && isMoveToEmpty) return true;
            if (!isTriggerPnlEmpty && isMoveToEmpty) return "Move To is required";
            if (isTriggerPnlEmpty && !isMoveToEmpty) return "Trigger PnL is required";
            return true;
        } else {
            return true;
        }
    }

    const validatePtpRow = (targetTp, exitQuantity) => {
        if (watchPtpEnabled) {
            const isTargetTpEmpty = !targetTp?.toString().trim();
            const isExitQuantityEmpty = !exitQuantity?.toString().trim();

            if (isTargetTpEmpty && isExitQuantityEmpty) return true;
            if (!isTargetTpEmpty && isExitQuantityEmpty) return "Exit Quantity is required";
            if (isTargetTpEmpty && !isExitQuantityEmpty) return "Target Tp is required";
            return true;
        } else {
            return true;
        }
    }

    const submitDynamicTrade = async (dynamicTrade) => {
        try {
            let validFlag = true;

            if (dynamicTrade.scaleEnabled && !(dynamicTrade.scale.scalingInEnabled || dynamicTrade.scale.scalingOutEnabled)) {
                validFlag = false;
                showError("Scale In or Scale Out is required.");
            }

            const validTslLevels = (dynamicTrade.tslLevels || []).filter(
                level => level.triggerPnl?.toString().trim() && level.moveTo?.toString().trim()
            );

            if (dynamicTrade.trailingStopLossEnabled && validTslLevels.length === 0) {
                validFlag = false;
                showError("At least one valid TSL Level is required.");
            }

            const validPtpLevels = (dynamicTrade.ptpLevels || []).filter(
                level => level.targetTp?.toString().trim() && level.exitQuantity?.toString().trim()
            );

            if (dynamicTrade.partialTakeProfitEnabled && validPtpLevels.length === 0) {
                validFlag = false;
                showError("At least one valid PTP Level is required.");
            }

            if (dynamicTrade.levelLockEnabled) {
                const validLevelLockConfigs = (dynamicTrade.levelLockConfigs || []).filter(
                    row => row.trigger?.toString().trim()
                );
                if (validLevelLockConfigs.length === 0) {
                    validFlag = false;
                    showError("At least one valid Ratchet Level is required.");
                }
            }

            const validDynamicTpRules = (dynamicTrade.dynamicTpRules || []).filter(
                row => row.type && row.exitPct?.toString().trim()
            );
            if (dynamicTrade.dynamicTakeProfitEnabled && validDynamicTpRules.length === 0) {
                validFlag = false;
                showError("At least one valid Dynamic TP rule is required.");
            }

            const validDynamicSlRules = (dynamicTrade.dynamicSlRules || []).filter(row => row.type);
            const hasHardStop = dynamicTrade.dynamicHardStopPercent?.toString().trim();
            if (dynamicTrade.dynamicStopLossEnabled && validDynamicSlRules.length === 0 && !hasHardStop) {
                validFlag = false;
                showError("Provide a Dynamic Hard Stop % or at least one Dynamic SL rule.");
            }

            if (validFlag) {
                dynamicTrade.tslLevels = dynamicTrade.tslLevels.filter(level => level.triggerPnl && level.moveTo);
                dynamicTrade.ptpLevels = dynamicTrade.ptpLevels.filter(level => level.targetTp && level.exitQuantity);

                dynamicTrade.dynamicTpRules = (dynamicTrade.dynamicTpRules || [])
                    .filter(row => row.type && row.exitPct?.toString().trim())
                    .map(row => ({
                        type: row.type,
                        triggerA: row.triggerA?.toString().trim() ? parseFloat(row.triggerA) : null,
                        atrMult: row.atrMult?.toString().trim() ? parseFloat(row.atrMult) : null,
                        exitPct: parseFloat(row.exitPct),
                    }));
                dynamicTrade.dynamicSlRules = (dynamicTrade.dynamicSlRules || [])
                    .filter(row => row.type)
                    .map(row => ({
                        type: row.type,
                        triggerA: row.triggerA?.toString().trim() ? parseFloat(row.triggerA) : null,
                        atrMult: row.atrMult?.toString().trim() ? parseFloat(row.atrMult) : null,
                    }));
                dynamicTrade.dynamicHardStopPercent = dynamicTrade.dynamicHardStopPercent?.toString().trim()
                    ? parseFloat(dynamicTrade.dynamicHardStopPercent) : null;
                dynamicTrade.atrPeriod = dynamicTrade.atrPeriod?.toString().trim() ? parseInt(dynamicTrade.atrPeriod, 10) : 14;
                dynamicTrade.atrRefreshSeconds = dynamicTrade.atrRefreshSeconds?.toString().trim()
                    ? parseInt(dynamicTrade.atrRefreshSeconds, 10) : 60;

                dynamicTrade.levelLockConfigs = (dynamicTrade.levelLockConfigs || [])
                    .filter(row => row.trigger?.toString().trim())
                    .map(row => ({
                        trigger: parseFloat(row.trigger),
                        add: parseFloat(row.add),
                        tsl: row.tsl?.toString().trim() ? parseFloat(row.tsl) : null,
                        cap: parseFloat(row.cap),
                        lockTarget: parseFloat(row.lockTarget),
                        lockFloor: parseFloat(row.lockFloor),
                    }));

                dynamicTrade.includeCoins = dynamicTrade.includeCoinValues ? dynamicTrade.includeCoinValues.trim().split('\n') : [];
                dynamicTrade.excludeCoins = dynamicTrade.excludeCoinValues ? dynamicTrade.excludeCoinValues.trim().split('\n') : [];


                delete dynamicTrade.hedge.crossAssetMap;
                delete dynamicTrade.hedge.baseSymbol;
                delete dynamicTrade.hedge.crossSymbol;
                dynamicTrade.hedge.tiers = (dynamicTrade.hedge.tiers || [])
                    .filter(t => t.drawdownPct !== '' && t.drawdownPct != null && t.allocationPct !== '' && t.allocationPct != null)
                    .map(({ tierType, ...rest }) => rest);
                if (dynamicTrade.hedge.takeProfitPct === '' || dynamicTrade.hedge.takeProfitPct === undefined) dynamicTrade.hedge.takeProfitPct = null;
                if (dynamicTrade.hedge.safetyExitPct === '' || dynamicTrade.hedge.safetyExitPct === undefined) dynamicTrade.hedge.safetyExitPct = null;
                if (dynamicTrade.hedge.harvestMainTriggerPct === '' || dynamicTrade.hedge.harvestMainTriggerPct === undefined) dynamicTrade.hedge.harvestMainTriggerPct = null;

                if (dynamicTrade.clientType === 'CLIENT_GROUP') {
                    const group = clientGroupList.find(g => g.id === dynamicTrade.clientGroupId);
                    const clientIds = getGroupValues(group);
                    if (clientIds.length === 0) {
                        showError("Selected group has no clients.");
                        return;
                    }
                    dynamicTrade.clientIds = clientIds;
                } else {
                    if (!dynamicTrade.clientId) {
                        showError("Please select a client.");
                        return;
                    }
                    dynamicTrade.clientIds = [dynamicTrade.clientId];
                    delete dynamicTrade.clientGroupId;
                }
                delete dynamicTrade.clientType;
                delete dynamicTrade.clientId;

                if (dynamicTrade.id) {
                    await DynamicTradeService.updateDynamicTrade(dynamicTrade);
                } else {
                    delete dynamicTrade.id;
                    await DynamicTradeService.createDynamicTrade(dynamicTrade);
                }
                showMessage("Dynamic order parameters saved successfully.");
                navigate('/dynamicTradeManager');
            }
        } catch (error) {
            showError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            {isLoading && <Loader />}
            <div className="section-wrapper">
                <div className="section-header">
                    {id ? 'Edit Dynamic Trade' : 'Create Dynamic Trade'}

                    <Button color="primary" variant="contained" disableElevation
                        onClick={() => navigate('/dynamicTradeManager')}>
                        Back
                    </Button>
                </div>
                <div>
                    <form onSubmit={handleSubmit(submitDynamicTrade)} autoComplete='off'>

                        <div className="row" style={{ paddingLeft: '0.5rem' }}>
                            <div className="col-12 col-xl-6 col-xxl-6">
                                <div className="form-control-wrapper">
                                    <label>Trade Name</label>
                                    <Controller
                                        name="tradeName"
                                        control={control}
                                        rules={{
                                            required: "Trade Name is required"
                                        }}
                                        render={({ field }) => (
                                            <TextField {...field} />
                                        )}
                                    />
                                    <div className="invalid-message pl-1">
                                        {errors.tradeName?.message}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-xl-6 col-xxl-6" style={{ paddingLeft: '1rem' }}>
                                <div className="form-control-wrapper">
                                    <Controller
                                        name="clientType"
                                        control={control}
                                        render={({ field }) => (
                                            <RadioGroup
                                                row
                                                {...field}
                                                onChange={(e) => {
                                                    field.onChange(e.target.value);
                                                    setValue('clientId', '');
                                                    setValue('clientGroupId', '');
                                                }}
                                            >
                                                <FormControlLabel value="CLIENT" control={<Radio size="small" />} label="Client" />
                                                <FormControlLabel value="CLIENT_GROUP" control={<Radio size="small" />} label="Client Group" />
                                            </RadioGroup>
                                        )}
                                    />

                                    {watchClientType === 'CLIENT' && (
                                        <Controller
                                            name="clientId"
                                            control={control}
                                            rules={{
                                                required: watchClientType === 'CLIENT' ? "Client is required" : false
                                            }}
                                            render={({ field }) => (
                                                <FormControl>
                                                    <Select {...field}>
                                                        {clientMenuItems}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        />
                                    )}

                                    {watchClientType === 'CLIENT_GROUP' && (
                                        <Controller
                                            name="clientGroupId"
                                            control={control}
                                            rules={{
                                                required: "Client Group is required"
                                            }}
                                            render={({ field }) => (
                                                <FormControl>
                                                    <Select
                                                        {...field}
                                                        displayEmpty
                                                        renderValue={(selected) => {
                                                            if (!selected) return <span style={{ color: '#999' }}>Select Group</span>;
                                                            const g = clientGroupList.find(x => x.id === selected);
                                                            return g?.name || '';
                                                        }}
                                                    >
                                                        {clientGroupMenuItems}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        />
                                    )}
                                    <div className="invalid-message">
                                        {errors.clientId?.message || errors.clientGroupId?.message}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row" style={{ paddingLeft: '0.5rem' }}>
                            <div className="col-12 col-lg-4 col-xl-4 col-xxl-4">
                                <div className="form-control-wrapper">
                                    <label>Order Type</label>
                                    <Controller
                                        name="orderType"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl>
                                                <Select {...field}>
                                                    {orderTypes.map((orderType) => (
                                                        <MenuItem key={orderType.dataValue} value={orderType.dataValue} >
                                                            {orderType.displayValue}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-lg-4 col-xl-4 col-xxl-4" style={{ paddingLeft: '1rem' }}>
                                <div className="form-control-wrapper">
                                    <label>Spread</label>
                                    <Controller
                                        name="spread"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField {...field} disabled={watchOrderType === 'MARKET'}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-lg-4 col-xl-4 col-xxl-4" style={{ paddingLeft: '1rem' }}>
                                <div className="form-control-wrapper">
                                    <label>Position</label>
                                    <Controller
                                        name="position"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField {...field} disabled />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div className="form-control-wrapper">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div>
                                            <Controller
                                                name="takeProfitEnabled"
                                                control={control}
                                                defaultValue={false}
                                                render={({ field }) => (
                                                    <Checkbox id="takeProfitEnabled"
                                                        {...field}
                                                        checked={field.value}
                                                        disabled={watchDynamicTpEnabled}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                    />
                                                )}
                                            />
                                            <label htmlFor="takeProfitEnabled" className="checkbox-label">Take Profit (%)</label>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <label htmlFor="dynamicTakeProfitEnabled" className="checkbox-label" style={{ fontSize: '0.75rem' }}>Dynamic</label>
                                            <Controller
                                                name="dynamicTakeProfitEnabled"
                                                control={control}
                                                defaultValue={false}
                                                render={({ field }) => (
                                                    <Switch id="dynamicTakeProfitEnabled" size="small"
                                                        {...field}
                                                        checked={field.value}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <Controller
                                        name="takeProfitPercent"
                                        control={control}
                                        rules={{
                                            required: (watchTakeProfitEnabled && !watchDynamicTpEnabled) ? "This field is required" : false
                                        }}
                                        render={({ field }) => (
                                            <TextField {...field} disabled={!watchTakeProfitEnabled || watchDynamicTpEnabled} style={{ paddingLeft: '0.5rem' }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                }}
                                            />
                                        )}
                                    />
                                    <div className="invalid-message pl-1">
                                        {errors.takeProfitPercent?.message}
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div className="form-control-wrapper">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div>
                                            <Controller
                                                name="stopLossEnabled"
                                                control={control}
                                                defaultValue={false}
                                                render={({ field }) => (
                                                    <Checkbox id="stopLossEnabled"
                                                        {...field}
                                                        checked={field.value}
                                                        disabled={watchDynamicSlEnabled}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                    />
                                                )}
                                            />
                                            <label htmlFor="stopLossEnabled" className="checkbox-label">Stop Loss (%)</label>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <label htmlFor="dynamicStopLossEnabled" className="checkbox-label" style={{ fontSize: '0.75rem' }}>Dynamic</label>
                                            <Controller
                                                name="dynamicStopLossEnabled"
                                                control={control}
                                                defaultValue={false}
                                                render={({ field }) => (
                                                    <Switch id="dynamicStopLossEnabled" size="small"
                                                        {...field}
                                                        checked={field.value}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <Controller

                                        name="stopLossPercent"
                                        control={control}
                                        rules={{
                                            required: (watchStopLossEnabled && !watchDynamicSlEnabled) ? "This field is required" : false
                                        }}
                                        render={({ field }) => (
                                            <TextField {...field} disabled={!watchStopLossEnabled || watchDynamicSlEnabled} style={{ paddingLeft: '0.5rem' }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                }}
                                            />
                                        )}
                                    />
                                    <div className="invalid-message pl-1">
                                        {errors.stopLossPercent?.message}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {(watchDynamicTpEnabled || watchDynamicSlEnabled) && (
                            <>
                                <div className="row pt-3" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}>
                                    <div className="col-12 mb-1">
                                        <label style={{ fontWeight: 600, fontSize: '12px', color: '#848e9c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Volatility Engine (drives Dynamic TP &amp; SL)
                                        </label>
                                    </div>
                                    <div className="col-12 col-lg-3 col-xl-3 col-xxl-3">
                                        <div className="form-control-wrapper">
                                            <label>ATR Interval</label>
                                            <Controller
                                                name="atrInterval"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl fullWidth>
                                                        <Select {...field}>
                                                            {atrIntervalOptions.map((opt) => (
                                                                <MenuItem key={opt.dataValue} value={opt.dataValue}>
                                                                    {opt.displayValue}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-3 col-xl-3 col-xxl-3">
                                        <div className="form-control-wrapper">
                                            <label>ATR Period</label>
                                            <Controller
                                                name="atrPeriod"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField {...field} type="number" />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-3 col-xl-3 col-xxl-3">
                                        <div className="form-control-wrapper">
                                            <label>ATR Refresh (sec)</label>
                                            <Controller
                                                name="atrRefreshSeconds"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField {...field} type="number" />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-3 col-xl-3 col-xxl-3">
                                        <div className="form-control-wrapper">
                                            <label>
                                                Hard Stop ROI % (neg)
                                                {' '}
                                                <span style={{ color: '#f6465d', fontSize: '10px', textTransform: 'none' }}>— Dynamic SL only</span>
                                            </label>
                                            <Controller
                                                name="dynamicHardStopPercent"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField {...field} disabled={!watchDynamicSlEnabled}
                                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row mt-2" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}>
                                    {/* ── Dynamic Take Profit Card ── */}
                                    <div className="col-12 col-xl-6 mb-3">
                                        <div style={{
                                            borderRadius: '8px',
                                            border: '1px solid #252525',
                                            borderLeft: watchDynamicTpEnabled ? '3px solid #0ecb81' : '3px solid #444',
                                            overflow: 'hidden',
                                            height: '100%'
                                        }}>
                                            <div style={{
                                                padding: '12px 16px',
                                                borderBottom: '1px solid #252525',
                                                background: watchDynamicTpEnabled ? 'rgba(14,203,129,0.06)' : 'rgba(255,255,255,0.03)'
                                            }}>
                                                <label style={{ fontWeight: 700, fontSize: '13px', color: '#0ecb81', display: 'block', lineHeight: 1.3, margin: 0 }}>
                                                    Dynamic Take Profit
                                                </label>
                                                <span style={{ fontSize: '11px', color: '#848e9c' }}>ATR / ladder / inventory / flat-$ tiers, evaluated in order</span>
                                            </div>
                                            <div style={{ padding: '16px', opacity: watchDynamicTpEnabled ? 1 : 0.4, pointerEvents: watchDynamicTpEnabled ? 'auto' : 'none' }}>
                                                <Table size="small" style={{ tableLayout: 'fixed' }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell style={{ ...RULE_HEADER_CELL_SX, width: RULE_COL_WIDTHS.type }}>Rule Type</TableCell>
                                                            <TableCell style={{ ...RULE_HEADER_CELL_SX, width: RULE_COL_WIDTHS.field }}>Field A</TableCell>
                                                            <TableCell style={{ ...RULE_HEADER_CELL_SX, width: RULE_COL_WIDTHS.field }}>Field B</TableCell>
                                                            <TableCell style={{ ...RULE_HEADER_CELL_SX, width: RULE_COL_WIDTHS.exit }}>Exit %</TableCell>
                                                            <TableCell style={{ padding: '5px 4px', width: RULE_COL_WIDTHS.del }} />
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {dynamicTpRuleFields.map((rowField, index) => {
                                                            const rowType = watchDynamicTpRules?.[index]?.type;
                                                            const meta = DYNAMIC_TP_RULE_TYPES.find(t => t.value === rowType) || DYNAMIC_TP_RULE_TYPES[0];
                                                            return (
                                                                <TableRow key={rowField.id}>
                                                                    <TableCell style={RULE_BODY_CELL_SX}>
                                                                        <Controller
                                                                            name={`dynamicTpRules.${index}.type`}
                                                                            control={control}
                                                                            defaultValue={rowField.type || 'DYNAMIC'}
                                                                            render={({ field }) => (
                                                                                <FormControl fullWidth size="small">
                                                                                    <Select {...field} sx={COMPACT_SELECT_SX}>
                                                                                        {DYNAMIC_TP_RULE_TYPES.map((opt) => (
                                                                                            <MenuItem key={opt.value} value={opt.value} sx={COMPACT_MENU_ITEM_SX}>
                                                                                                {opt.label}
                                                                                            </MenuItem>
                                                                                        ))}
                                                                                    </Select>
                                                                                </FormControl>
                                                                            )}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell style={RULE_BODY_CELL_SX}>
                                                                        {meta.aLabel ? (
                                                                            <Controller
                                                                                name={`dynamicTpRules.${index}.triggerA`}
                                                                                control={control}
                                                                                render={({ field }) => (
                                                                                    <TextField {...field} size="small" fullWidth placeholder={meta.aLabel} />
                                                                                )}
                                                                            />
                                                                        ) : (
                                                                            <div style={{ fontSize: '11px', color: '#5b6472', textAlign: 'center', padding: '8px 0' }}>N/A</div>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell style={RULE_BODY_CELL_SX}>
                                                                        {meta.bLabel ? (
                                                                            <Controller
                                                                                name={`dynamicTpRules.${index}.atrMult`}
                                                                                control={control}
                                                                                render={({ field }) => (
                                                                                    <TextField {...field} size="small" fullWidth placeholder={meta.bLabel} />
                                                                                )}
                                                                            />
                                                                        ) : (
                                                                            <div style={{ fontSize: '11px', color: '#5b6472', textAlign: 'center', padding: '8px 0' }}>N/A</div>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell style={RULE_BODY_CELL_SX}>
                                                                        <Controller
                                                                            name={`dynamicTpRules.${index}.exitPct`}
                                                                            control={control}
                                                                            render={({ field }) => (
                                                                                <TextField {...field} size="small" fullWidth />
                                                                            )}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell style={{ padding: '4px 4px', width: RULE_COL_WIDTHS.del }}>
                                                                        <IconButton size="small" onClick={() => removeDynamicTpRule(index)} style={{ color: '#ef4444' }}>
                                                                            <DeleteOutlineIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                                <Button size="small" color="primary"
                                                    onClick={() => appendDynamicTpRule({ type: 'DYNAMIC', triggerA: '', atrMult: '', exitPct: '' })}
                                                    startIcon={<AddCircleOutlineIcon />}
                                                    style={{ fontSize: '11px', marginTop: '8px' }}>
                                                    Add Rule
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ── Dynamic Stop Loss Card ── */}
                                    <div className="col-12 col-xl-6 mb-3">
                                        <div style={{
                                            borderRadius: '8px',
                                            border: '1px solid #252525',
                                            borderLeft: watchDynamicSlEnabled ? '3px solid #f6465d' : '3px solid #444',
                                            overflow: 'hidden',
                                            height: '100%'
                                        }}>
                                            <div style={{
                                                padding: '12px 16px',
                                                borderBottom: '1px solid #252525',
                                                background: watchDynamicSlEnabled ? 'rgba(246,70,93,0.06)' : 'rgba(255,255,255,0.03)'
                                            }}>
                                                <label style={{ fontWeight: 700, fontSize: '13px', color: '#f6465d', display: 'block', lineHeight: 1.3, margin: 0 }}>
                                                    Dynamic Stop Loss
                                                </label>
                                                <span style={{ fontSize: '11px', color: '#848e9c' }}>Combined into one ratcheting floor — most protective wins</span>
                                            </div>
                                            <div style={{ padding: '16px', opacity: watchDynamicSlEnabled ? 1 : 0.4, pointerEvents: watchDynamicSlEnabled ? 'auto' : 'none' }}>
                                                <Table size="small" style={{ tableLayout: 'fixed' }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell style={{ ...RULE_HEADER_CELL_SX, width: RULE_COL_WIDTHS.type }}>Rule Type</TableCell>
                                                            <TableCell style={{ ...RULE_HEADER_CELL_SX, width: RULE_COL_WIDTHS.field }}>Field A</TableCell>
                                                            <TableCell style={{ ...RULE_HEADER_CELL_SX, width: RULE_COL_WIDTHS.field }}>Field B</TableCell>
                                                            <TableCell style={{ padding: '5px 4px', width: RULE_COL_WIDTHS.del }} />
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {dynamicSlRuleFields.map((rowField, index) => {
                                                            const rowType = watchDynamicSlRules?.[index]?.type;
                                                            const meta = DYNAMIC_SL_RULE_TYPES.find(t => t.value === rowType) || DYNAMIC_SL_RULE_TYPES[0];
                                                            return (
                                                                <TableRow key={rowField.id}>
                                                                    <TableCell style={RULE_BODY_CELL_SX}>
                                                                        <Controller
                                                                            name={`dynamicSlRules.${index}.type`}
                                                                            control={control}
                                                                            defaultValue={rowField.type || 'BREAK_EVEN'}
                                                                            render={({ field }) => (
                                                                                <FormControl fullWidth size="small">
                                                                                    <Select {...field} sx={COMPACT_SELECT_SX}>
                                                                                        {DYNAMIC_SL_RULE_TYPES.map((opt) => (
                                                                                            <MenuItem key={opt.value} value={opt.value} sx={COMPACT_MENU_ITEM_SX}>
                                                                                                {opt.label}
                                                                                            </MenuItem>
                                                                                        ))}
                                                                                    </Select>
                                                                                </FormControl>
                                                                            )}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell style={RULE_BODY_CELL_SX}>
                                                                        {meta.aLabel ? (
                                                                            <Controller
                                                                                name={`dynamicSlRules.${index}.triggerA`}
                                                                                control={control}
                                                                                render={({ field }) => (
                                                                                    <TextField {...field} size="small" fullWidth placeholder={meta.aLabel} />
                                                                                )}
                                                                            />
                                                                        ) : (
                                                                            <div style={{ fontSize: '11px', color: '#5b6472', textAlign: 'center', padding: '8px 0' }}>N/A</div>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell style={RULE_BODY_CELL_SX}>
                                                                        {meta.bLabel ? (
                                                                            <Controller
                                                                                name={`dynamicSlRules.${index}.atrMult`}
                                                                                control={control}
                                                                                render={({ field }) => (
                                                                                    <TextField {...field} size="small" fullWidth placeholder={meta.bLabel} />
                                                                                )}
                                                                            />
                                                                        ) : (
                                                                            <div style={{ fontSize: '11px', color: '#5b6472', textAlign: 'center', padding: '8px 0' }}>N/A</div>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell style={{ padding: '4px 4px', width: RULE_COL_WIDTHS.del }}>
                                                                        <IconButton size="small" onClick={() => removeDynamicSlRule(index)} style={{ color: '#ef4444' }}>
                                                                            <DeleteOutlineIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                                <Button size="small" color="primary"
                                                    onClick={() => appendDynamicSlRule({ type: 'BREAK_EVEN', triggerA: '', atrMult: '' })}
                                                    startIcon={<AddCircleOutlineIcon />}
                                                    style={{ fontSize: '11px', marginTop: '8px' }}>
                                                    Add Rule
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="row">
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div>
                                    <Controller
                                        name="scaleEnabled"
                                        control={control}
                                        defaultValue={false}
                                        render={({ field }) => (
                                            <Checkbox id="scaleEnabled"
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        )}
                                    />
                                    <label htmlFor="scaleEnabled" className="checkbox-label">Scaling</label>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div>
                                    <Controller
                                        name="scale.scalingInEnabled"
                                        control={control}
                                        defaultValue={false}
                                        render={({ field }) => (
                                            <Checkbox id="scalingInEnabled"
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                disabled={!watchScaleEnabled}
                                            />
                                        )}
                                    />
                                    <label htmlFor="scalingInEnabled" className="checkbox-label">Scale In</label>
                                </div>
                                <div>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Trigger PnL ≥</TableCell>
                                                <TableCell>Qty</TableCell>
                                                <TableCell>Min/Max (USDT)</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <Controller

                                                        name="scale.scaleIn.triggerPnl"
                                                        control={control}
                                                        rules={{
                                                            required: watchScaleInEnabled ? "This field is required" : false
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchScaleInEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <div className="invalid-message pt-1">
                                                        {errors.scale?.scaleIn?.triggerPnl?.message}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Controller

                                                        name="scale.scaleIn.quantity"
                                                        control={control}
                                                        rules={{
                                                            required: watchScaleInEnabled ? "This field is required" : false
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchScaleInEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <div className="invalid-message pt-1">
                                                        {errors.scale?.scaleIn?.quantity?.message}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name="scale.scaleIn.minMaxUsdt"
                                                        control={control}
                                                        rules={{
                                                            required: watchScaleInEnabled ? "This field is required" : false
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchScaleInEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <div className="invalid-message pt-1">
                                                        {errors.scale?.scaleIn?.minMaxUsdt?.message}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div>
                                    <Controller
                                        name="scale.scalingOutEnabled"
                                        control={control}
                                        defaultValue={false}
                                        render={({ field }) => (
                                            <Checkbox id="scalingOutEnabled"
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                disabled={!watchScaleEnabled}
                                            />
                                        )}
                                    />
                                    <label htmlFor="scalingOutEnabled" className="checkbox-label">Scale Out</label>
                                </div>
                                <div>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Trigger PnL ≥</TableCell>
                                                <TableCell>Qty</TableCell>
                                                <TableCell>Min/Max (USDT)</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <Controller
                                                        name="scale.scaleOut.triggerPnl"
                                                        control={control}
                                                        rules={{
                                                            required: watchScaleOutEnabled ? "This field is required" : false
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchScaleOutEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <div className="invalid-message pt-1">
                                                        {errors.scale?.scaleOut?.triggerPnl?.message}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Controller

                                                        name="scale.scaleOut.quantity"
                                                        control={control}
                                                        rules={{
                                                            required: watchScaleOutEnabled ? "This field is required" : false
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchScaleOutEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <div className="invalid-message pt-1">
                                                        {errors.scale?.scaleOut?.quantity?.message}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name="scale.scaleOut.minMaxUsdt"
                                                        control={control}
                                                        rules={{
                                                            required: watchScaleOutEnabled ? "This field is required" : false
                                                        }}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchScaleOutEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    <div className="invalid-message pt-1">
                                                        {errors.scale?.scaleOut?.minMaxUsdt?.message}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                        <div className="row pt-3">
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div>
                                    <Controller
                                        name="trailingStopLossEnabled"
                                        control={control}
                                        defaultValue={false}
                                        render={({ field }) => (
                                            <Checkbox id="trailingStopLossEnabled"
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        )}
                                    />
                                    <label htmlFor="trailingStopLossEnabled" className="checkbox-label">Trailing Stop-Loss (TSL) Levels</label>
                                </div>
                                <div>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Trigger PnL ≥</TableCell>
                                                <TableCell>Move SL to</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {trailingStopLossLevelFields.map((trailingStopLossLevelField, index) => (
                                                <TableRow key={trailingStopLossLevelField.id}>
                                                    <TableCell>
                                                        <Controller

                                                            name={`tslLevels.${index}.triggerPnl`}
                                                            control={control}
                                                            rules={{
                                                                validate: (value) => {
                                                                    const moveTo = getValues(`tslLevels.${index}.moveTo`);
                                                                    const result = validateTslRow(value, moveTo);
                                                                    return result === true || result === "Trigger PnL is required" ? result : true;
                                                                }
                                                            }}
                                                            render={({ field }) => (
                                                                <TextField {...field} disabled={!watchTslEnabled}
                                                                    InputProps={{
                                                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        <div className="invalid-message pt-1">
                                                            {errors.tslLevels?.[index]?.triggerPnl?.message}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Controller

                                                            name={`tslLevels.${index}.moveTo`}
                                                            control={control}
                                                            rules={{
                                                                validate: (value) => {
                                                                    const triggerPnl = getValues(`tslLevels.${index}.triggerPnl`);
                                                                    const result = validateTslRow(triggerPnl, value);
                                                                    return result === true || result === "Move To is required" ? result : true;
                                                                }
                                                            }}
                                                            render={({ field }) => (
                                                                <TextField {...field} disabled={!watchTslEnabled}
                                                                    InputProps={{
                                                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        <div className="invalid-message pt-1">
                                                            {errors.tslLevels?.[index]?.moveTo?.message}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>

                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div>
                                    <Controller
                                        name="partialTakeProfitEnabled"
                                        control={control}
                                        defaultValue={false}
                                        render={({ field }) => (
                                            <Checkbox id="partialTakeProfitEnabled"
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        )}
                                    />
                                    <label htmlFor="partialTakeProfitEnabled" className="checkbox-label">Partial Take Profit (PTP)</label>
                                </div>
                                <div className="px-3">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Target TP (%)</TableCell>
                                                <TableCell>Exit Qty (%)</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {partialTakeProfitFields.map((partialTakeProfitField, index) => (
                                                <TableRow key={partialTakeProfitField.id}>
                                                    <TableCell>
                                                        <Controller

                                                            name={`ptpLevels.${index}.targetTp`}
                                                            control={control}
                                                            rules={{
                                                                validate: (value) => {
                                                                    const exitQuantity = getValues(`ptpLevels.${index}.exitQuantity`);
                                                                    const result = validatePtpRow(value, exitQuantity);
                                                                    return result === true || result === "Target Tp is required" ? result : true;
                                                                }
                                                            }}
                                                            render={({ field }) => (
                                                                <TextField {...field} disabled={!watchPtpEnabled}
                                                                    InputProps={{
                                                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        <div className="invalid-message pt-1">
                                                            {errors.ptpLevels?.[index]?.targetTp?.message}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Controller

                                                            name={`ptpLevels.${index}.exitQuantity`}
                                                            control={control}
                                                            rules={{
                                                                validate: (value) => {
                                                                    const targetTp = getValues(`ptpLevels.${index}.targetTp`);
                                                                    const result = validatePtpRow(targetTp, value);
                                                                    return result === true || result === "Exit Quantity is required" ? result : true;
                                                                }
                                                            }}
                                                            render={({ field }) => (
                                                                <TextField {...field} disabled={!watchPtpEnabled}
                                                                    InputProps={{
                                                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        <div className="invalid-message pt-1">
                                                            {errors.ptpLevels?.[index]?.exitQuantity?.message}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                        <div className="row pt-3" style={{ paddingLeft: '0.5rem' }}>
                            <div className="col-12 col-lg-6 col-xl-4 col-xxl-4">
                                <div className="form-control-wrapper">
                                    <label>Final TP Exit at PnL ≥ (%)</label>
                                    <Controller

                                        name="finalTpExitPercent"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField {...field}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                }}
                                            />
                                        )}
                                    />
                                    <div className="invalid-message">
                                        {errors.type?.message}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div>
                                    <Controller
                                        name="autoLoadAllCoinsEnabled"
                                        control={control}
                                        defaultValue={false}
                                        render={({ field }) => (
                                            <Checkbox id="autoLoadAllCoinsEnabled"
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        )}
                                    />
                                    <label htmlFor="autoLoadAllCoinsEnabled" className="checkbox-label">Load All Coins Automatically</label>
                                </div>
                            </div>
                        </div>
                        <div className="row" style={{ paddingLeft: '0.5rem' }}>
                            {!watchAutoLoadAllCoinsEnabled && (
                                <div className="col-12 col-lg-6 col-xl-4 col-xxl-4">
                                    <div className="form-control-wrapper">
                                        <label>Include Coins</label>
                                        <Controller
                                            name="includeCoinValues"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    multiline
                                                    maxRows={5}
                                                />

                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="col-12 col-lg-6 col-xl-4 col-xxl-4">
                                <div className="form-control-wrapper">
                                    <label>Exclude Coins</label>
                                    <Controller
                                        name="excludeCoinValues"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                multiline
                                                maxRows={5}
                                            />

                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row mb-2">
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div>
                                    <Controller
                                        name="hedge.enabled"
                                        control={control}
                                        defaultValue={false}
                                        render={({ field }) => (
                                            <Checkbox id="hedgeEnabled"
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        )}
                                    />
                                    <label htmlFor="hedgeEnabled" className="checkbox-label">Hedge</label>
                                </div>
                            </div>
                        </div>
                        {/* ── Hedge Strategy Cards ── */}
                        {(
                            <div style={{ opacity: watchHedgeEnabled ? 1 : 0.45, pointerEvents: watchHedgeEnabled ? 'auto' : 'none' }}>
                                {/* Direction / Symbol configuration — hidden from UI, values preserved in form state */}
                                <div className="row mt-2" style={{ display: 'none' }}>
                                    <div className="col-12 col-lg-3 col-xl-3 col-xxl-3">
                                        <div className="form-control-wrapper">
                                            <label>Direction</label>
                                            <Controller
                                                name="hedge.direction"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl>
                                                        <Select {...field}>
                                                            {directionTypes.map((direction) => (
                                                                <MenuItem key={direction.dataValue} value={direction.dataValue}>
                                                                    {direction.displayValue}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-3 col-xl-3 col-xxl-3">
                                        <div className="form-control-wrapper">
                                            <label>Base Symbol(s)</label>
                                            <Controller
                                                name="hedge.baseSymbol"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl>
                                                        <Select
                                                            {...field}
                                                            onChange={(e) => {
                                                                field.onChange(e);
                                                                setValue('hedge.allPositionCoins', e.target.value === 'ALL');
                                                            }}
                                                        >
                                                            {baseSymbolList.map((baseSymbol) => (
                                                                <MenuItem key={baseSymbol.dataValue} value={baseSymbol.dataValue}>
                                                                    {baseSymbol.displayValue}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-3 col-xl-3 col-xxl-3">
                                        <div className="form-control-wrapper">
                                            <label>Hedge Type</label>
                                            <Controller
                                                name="hedge.type"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl>
                                                        <Select {...field}>
                                                            {hedgeTypes.map((hedgeType) => (
                                                                <MenuItem key={hedgeType.dataValue} value={hedgeType.dataValue}>
                                                                    {hedgeType.displayValue}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-3 col-xl-3 col-xxl-3">
                                        <div className="form-control-wrapper">
                                            <label>Cross Symbol(s)</label>
                                            <Controller
                                                name="hedge.crossSymbol"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl>
                                                        <Select {...field} disabled={watchHedgeType !== 'CROSS_ASSET'}>
                                                            {crossSymbolList.map((crossSymbol) => (
                                                                <MenuItem key={crossSymbol.dataValue} value={crossSymbol.dataValue}>
                                                                    {crossSymbol.displayValue}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row mb-2" style={{ display: 'none' }}>
                                    <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                        <div className="form-control-wrapper">
                                            <label>Base Symbols Coins</label>
                                            <Controller
                                                appendTo="body"
                                                name="includeSymbols"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        multiline
                                                        minRows={3}
                                                        maxRows={3}
                                                        placeholder="Enter coins (comma seperated)"
                                                        disabled={watchBaseSymbols !== 'ADD'}
                                                    />
                                                )}
                                            />
                                            <div className="invalid-message">{errors.includeSymbols?.message}</div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                        <div className="form-control-wrapper">
                                            <label>Cross Symbols Coins</label>
                                            <Controller
                                                appendTo="body"
                                                name="includeSymbols"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        multiline
                                                        minRows={3}
                                                        maxRows={3}
                                                        placeholder="Enter coins (comma seperated)"
                                                        disabled={watchHedgeType !== 'CROSS_ASSET'}
                                                    />
                                                )}
                                            />
                                            <div className="invalid-message">{errors.includeSymbols?.message}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Migration warning: hedge enabled but neither sub-flag is on */}
                                {!watchProfitHedgeEnabled && !watchLossHedgeEnabled && (
                                    <div className="row mt-3" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}>
                                        <div className="col-12">
                                            <div style={{
                                                background: 'rgba(246,166,0,0.07)',
                                                border: '1px solid rgba(246,166,0,0.25)',
                                                borderRadius: '6px',
                                                padding: '10px 14px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}>
                                                <span style={{ fontSize: '15px' }}>⚠</span>
                                                <span style={{ fontSize: '12px', color: '#f6a600' }}>
                                                    Hedge is enabled but neither <strong>Loss Hedge</strong> nor <strong>Profit Hedge</strong> is active —
                                                    enable at least one sub-strategy below.
                                                    Existing records saved before this update need to re-enable the relevant strategy.
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="row mt-3" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}>

                                    {/* ══ Loss Hedge Card ══ */}
                                    <div className="col-12 col-xl-6 mb-3">
                                        <div style={{
                                            borderRadius: '8px',
                                            border: '1px solid #252525',
                                            borderLeft: watchLossHedgeEnabled ? '3px solid #f6465d' : '3px solid #444',
                                            background: watchLossHedgeEnabled ? 'rgba(246,70,93,0.03)' : 'rgba(255,255,255,0.015)',
                                            overflow: 'hidden',
                                            height: '100%'
                                        }}>
                                            {/* Card Header */}
                                            <div style={{
                                                padding: '12px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                borderBottom: '1px solid #252525',
                                                background: watchLossHedgeEnabled ? 'rgba(246,70,93,0.06)' : 'rgba(255,255,255,0.03)'
                                            }}>
                                                <Controller
                                                    name="hedge.lossHedgeEnabled"
                                                    control={control}
                                                    defaultValue={false}
                                                    render={({ field }) => (
                                                        <Checkbox
                                                            id="lossHedgeEnabled"
                                                            {...field}
                                                            checked={field.value}
                                                            onChange={(e) => field.onChange(e.target.checked)}
                                                            size="small"
                                                            sx={{ color: '#f6465d', '&.Mui-checked': { color: '#f6465d' }, padding: '4px', marginRight: '8px' }}
                                                        />
                                                    )}
                                                />
                                                <div>
                                                    <label htmlFor="lossHedgeEnabled" style={{ fontWeight: 700, fontSize: '13px', color: '#f6465d', cursor: 'pointer', display: 'block', lineHeight: 1.3, margin: 0 }}>
                                                        Loss Hedge
                                                    </label>
                                                    <span style={{ fontSize: '11px', color: '#848e9c' }}> Triggers when primary is in loss</span>
                                                </div>
                                            </div>

                                            {/* Card Body */}
                                            {(
                                                <div style={{ padding: '16px', opacity: watchLossHedgeEnabled ? 1 : 0.4, pointerEvents: watchLossHedgeEnabled ? 'auto' : 'none' }}>
                                                    <label style={{ fontSize: '11px', color: '#848e9c', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                                                        Drawdown Tiers
                                                    </label>
                                                    <Table size="small" style={{ marginTop: '6px', marginBottom: '4px' }}>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell style={{ fontSize: '11px', padding: '5px 8px', color: '#848e9c' }}>Drawdown %</TableCell>
                                                                <TableCell style={{ fontSize: '11px', padding: '5px 8px', color: '#848e9c' }}>Allocation %</TableCell>
                                                                <TableCell style={{ padding: '5px 4px' }} />
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {hedgeTierFields.map((tierField, index) => {
                                                                if (tierField.tierType !== 'LOSS') return null;
                                                                const lossTierCount = hedgeTierFields.filter(f => f.tierType === 'LOSS').length;
                                                                return (
                                                                    <TableRow key={tierField.id}>
                                                                        <TableCell style={{ padding: '4px 8px' }}>
                                                                            <Controller
                                                                                name={`hedge.tiers.${index}.drawdownPct`}
                                                                                control={control}
                                                                                render={({ field }) => (
                                                                                    <TextField {...field} type="number" fullWidth size="small"
                                                                                        inputProps={{ step: 0.01 }}
                                                                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell style={{ padding: '4px 8px' }}>
                                                                            <Controller
                                                                                name={`hedge.tiers.${index}.allocationPct`}
                                                                                control={control}
                                                                                render={({ field }) => (
                                                                                    <TextField {...field} type="number" fullWidth size="small"
                                                                                        inputProps={{ step: 1 }}
                                                                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell style={{ padding: '4px 4px', width: '36px' }}>
                                                                            <Tooltip title={lossTierCount <= 1 ? 'At least one loss tier required' : 'Remove tier'}>
                                                                                <span>
                                                                                    <IconButton size="small"
                                                                                        disabled={lossTierCount <= 1}
                                                                                        onClick={() => removeHedgeTier(index)}
                                                                                        style={{ color: lossTierCount <= 1 ? undefined : '#ef4444' }}>
                                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </span>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                    <Button size="small"
                                                        disabled={hasIncompleteLossTier}
                                                        onClick={() => appendHedgeTier({ drawdownPct: -0.5, allocationPct: 100, tierType: 'LOSS' })}
                                                        startIcon={<AddCircleOutlineIcon />}
                                                        style={{ fontSize: '11px', marginBottom: '16px' }}>
                                                        Add Loss Tier
                                                    </Button>

                                                    <div style={{ borderTop: '1px solid #252525', paddingTop: '14px' }}>
                                                        <label style={{ fontSize: '11px', color: '#848e9c', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, display: 'block', marginBottom: '10px' }}>
                                                            Net-Zero Harvesting
                                                        </label>
                                                        <div className="row g-2">
                                                            <div className="col-6">
                                                                <div className="form-control-wrapper">
                                                                    <label>Harvest Trigger (%)</label>
                                                                    <Controller
                                                                        name="hedge.harvestTriggerPct"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <TextField {...field} type="number"
                                                                                inputProps={{ step: 0.01 }}
                                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-6">
                                                                <div className="form-control-wrapper">
                                                                    <label>Fee Factor</label>
                                                                    <Controller
                                                                        name="hedge.feeFactor"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <TextField {...field} type="number"
                                                                                inputProps={{ step: 0.0001 }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-6">
                                                                <div className="form-control-wrapper">
                                                                    <label>Harvest Main Trigger (%)</label>
                                                                    <Controller
                                                                        name="hedge.harvestMainTriggerPct"
                                                                        control={control}
                                                                        render={({ field: { value, onChange, ...rest } }) => (
                                                                            <TextField
                                                                                {...rest}
                                                                                value={value ?? ''}
                                                                                onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                                                                type="number"
                                                                                placeholder="Leave blank to disable"
                                                                                inputProps={{ step: 0.01 }}
                                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div style={{ borderTop: '1px solid #252525', paddingTop: '14px', marginTop: '14px' }}>
                                                        <label style={{ fontSize: '11px', color: '#848e9c', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, display: 'block', marginBottom: '10px' }}>
                                                            Unwind / Recovery
                                                        </label>
                                                        <div className="row g-2 align-items-center">
                                                            <div className="col-6" style={{ display: 'flex', alignItems: 'center' }}>
                                                                <Controller
                                                                    name="hedge.unwindEnabled"
                                                                    control={control}
                                                                    defaultValue={false}
                                                                    render={({ field }) => (
                                                                        <Checkbox
                                                                            id="hedgeUnwindEnabled"
                                                                            {...field}
                                                                            checked={field.value}
                                                                            onChange={(e) => field.onChange(e.target.checked)}
                                                                            size="small"
                                                                            sx={{ padding: '4px', marginRight: '6px' }}
                                                                        />
                                                                    )}
                                                                />
                                                                <label htmlFor="hedgeUnwindEnabled" className="checkbox-label">Enable Unwind</label>
                                                            </div>
                                                            <div className="col-6">
                                                                <div className="form-control-wrapper">
                                                                    <label>Unwind Buffer (%)</label>
                                                                    <Controller
                                                                        name="hedge.unwindBufferPct"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <TextField {...field} disabled={!watchHedgeUnwindEnabled}
                                                                                type="number" inputProps={{ step: 0.01 }}
                                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* ══ Profit Hedge Card ══ */}
                                    <div className="col-12 col-xl-6 mb-3">
                                        <div style={{
                                            borderRadius: '8px',
                                            border: '1px solid #252525',
                                            borderLeft: watchProfitHedgeEnabled ? '3px solid #0ecb81' : '3px solid #444',
                                            background: watchProfitHedgeEnabled ? 'rgba(14,203,129,0.03)' : 'rgba(255,255,255,0.015)',
                                            overflow: 'hidden',
                                            height: '100%'
                                        }}>
                                            {/* Card Header */}
                                            <div style={{
                                                padding: '12px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                borderBottom: '1px solid #252525',
                                                background: watchProfitHedgeEnabled ? 'rgba(14,203,129,0.06)' : 'rgba(255,255,255,0.03)'
                                            }}>
                                                <Controller
                                                    name="hedge.profitHedgeEnabled"
                                                    control={control}
                                                    defaultValue={false}
                                                    render={({ field }) => (
                                                        <Checkbox
                                                            id="profitHedgeEnabled"
                                                            {...field}
                                                            checked={field.value}
                                                            onChange={(e) => field.onChange(e.target.checked)}
                                                            size="small"
                                                            sx={{ color: '#0ecb81', '&.Mui-checked': { color: '#0ecb81' }, padding: '4px', marginRight: '8px' }}
                                                        />
                                                    )}
                                                />
                                                <div>
                                                    <label htmlFor="profitHedgeEnabled" style={{ fontWeight: 700, fontSize: '13px', color: '#0ecb81', cursor: 'pointer', display: 'block', lineHeight: 1.3, margin: 0 }}>
                                                        Profit Hedge
                                                    </label>
                                                    <span style={{ fontSize: '11px', color: '#848e9c' }}> Triggers when primary is in profit</span>
                                                </div>
                                            </div>

                                            {/* Card Body */}
                                            {(
                                                <div style={{ padding: '16px', opacity: watchProfitHedgeEnabled ? 1 : 0.4, pointerEvents: watchProfitHedgeEnabled ? 'auto' : 'none' }}>
                                                    <label style={{ fontSize: '11px', color: '#848e9c', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                                                        Profit Tiers
                                                    </label>
                                                    <Table size="small" style={{ marginTop: '6px', marginBottom: '4px' }}>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell style={{ fontSize: '11px', padding: '5px 8px', color: '#848e9c' }}>Profit %</TableCell>
                                                                <TableCell style={{ fontSize: '11px', padding: '5px 8px', color: '#848e9c' }}>Allocation %</TableCell>
                                                                <TableCell style={{ padding: '5px 4px' }} />
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {hedgeTierFields.map((tierField, index) => {
                                                                if (tierField.tierType !== 'PROFIT') return null;
                                                                return (
                                                                    <TableRow key={tierField.id}>
                                                                        <TableCell style={{ padding: '4px 8px' }}>
                                                                            <Controller
                                                                                name={`hedge.tiers.${index}.drawdownPct`}
                                                                                control={control}
                                                                                render={({ field }) => (
                                                                                    <TextField {...field} type="number" fullWidth size="small"
                                                                                        inputProps={{ step: 0.01 }}
                                                                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell style={{ padding: '4px 8px' }}>
                                                                            <Controller
                                                                                name={`hedge.tiers.${index}.allocationPct`}
                                                                                control={control}
                                                                                render={({ field }) => (
                                                                                    <TextField {...field} type="number" fullWidth size="small"
                                                                                        inputProps={{ step: 1 }}
                                                                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell style={{ padding: '4px 4px', width: '36px' }}>
                                                                            <Tooltip title="Remove tier">
                                                                                <IconButton size="small"
                                                                                    onClick={() => removeHedgeTier(index)}
                                                                                    style={{ color: '#ef4444' }}>
                                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                    <Button size="small"
                                                        disabled={hasIncompleteProfitTier}
                                                        onClick={() => appendHedgeTier({ drawdownPct: 0.05, allocationPct: 30, tierType: 'PROFIT' })}
                                                        startIcon={<AddCircleOutlineIcon />}
                                                        style={{ fontSize: '11px', marginBottom: '16px' }}>
                                                        Add Profit Tier
                                                    </Button>

                                                    <div style={{ borderTop: '1px solid #252525', paddingTop: '14px' }}>
                                                        <label style={{ fontSize: '11px', color: '#848e9c', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, display: 'block', marginBottom: '10px' }}>
                                                            Take Profit / Safety Exit
                                                        </label>
                                                        <div className="row g-2">
                                                            <div className="col-6">
                                                                <div className="form-control-wrapper">
                                                                    <label>Take Profit % (blank = off)</label>
                                                                    <Controller
                                                                        name="hedge.takeProfitPct"
                                                                        control={control}
                                                                        render={({ field: { value, onChange, ...rest } }) => (
                                                                            <TextField
                                                                                {...rest}
                                                                                value={value ?? ''}
                                                                                onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                                                                type="number"
                                                                                inputProps={{ step: 0.01 }}
                                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-6">
                                                                <div className="form-control-wrapper">
                                                                    <label>Safety Exit % (blank = off)</label>
                                                                    <Controller
                                                                        name="hedge.safetyExitPct"
                                                                        control={control}
                                                                        render={({ field: { value, onChange, ...rest } }) => (
                                                                            <TextField
                                                                                {...rest}
                                                                                value={value ?? ''}
                                                                                onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                                                                                type="number"
                                                                                inputProps={{ step: 0.01 }}
                                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* ── General Settings ── */}
                                <div className="row mb-3" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}>
                                    <div className="col-12 mb-2">
                                        <label style={{ fontWeight: 600, fontSize: '12px', color: '#848e9c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            General Settings
                                        </label>
                                    </div>
                                    <div className="col-12 col-lg-4">
                                        <div className="form-control-wrapper">
                                            <label>Min Notional (USD)</label>
                                            <Controller
                                                name="hedge.minNotionalUsd"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField {...field} type="number"
                                                        inputProps={{ step: 0.5 }}
                                                        InputProps={{ endAdornment: <InputAdornment position="end">$</InputAdornment> }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-4">
                                        <div className="form-control-wrapper">
                                            <label>TTL Hours</label>
                                            <Controller
                                                name="hedge.ttlHours"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField {...field} type="number"
                                                        inputProps={{ step: 1, min: 1 }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-4">
                                        <div className="form-control-wrapper">
                                            <label>Max Hedge Cap (%)</label>
                                            <Controller
                                                name="hedge.maxHedgeCapPct"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextField {...field} type="number" fullWidth
                                                        inputProps={{ step: 1, min: 0, max: 300 }}
                                                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Legacy Bands (backward compat) ── */}
                        {/* <div className="row mt-1 mb-1" style={{ paddingLeft: '0.5rem' }}>
                            <div className="col-12">
                                <label className="checkbox-label" style={{ fontWeight: 600, fontSize: '12px', color: '#848e9c', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Legacy Bands (used only when Tiers are empty)
                                </label>
                            </div>
                        </div>
                        <div className="row mb-3" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}>
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div>
                                    <label style={{ paddingLeft: '0.5rem', fontSize: '11px', color: '#848e9c' }}>Profit Range</label>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>From</TableCell>
                                                <TableCell>To</TableCell>
                                                <TableCell>Allocation (%)</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <Controller
                                                        name="hedge.profitFrom"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchHedgeEnabled} />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name="hedge.profitTo"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchHedgeEnabled} />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name="hedge.profitAllocationPct"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchHedgeEnabled}
                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                            <div className="col-12 col-lg-6 col-xl-6 col-xxl-6">
                                <div>
                                    <label style={{ paddingLeft: '0.5rem', fontSize: '11px', color: '#848e9c' }}>Loss Range</label>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>From</TableCell>
                                                <TableCell>To</TableCell>
                                                <TableCell>Allocation (%)</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <Controller
                                                        name="hedge.lossFrom"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchHedgeEnabled} />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name="hedge.lossTo"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchHedgeEnabled} />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name="hedge.lossAllocationPct"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchHedgeEnabled}
                                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        </div> */}


                        {/* Ratchet (Level Lock) */}
                        <div className="row mb-2 mt-3">
                            <div className="col-12">
                                <div>
                                    <Controller
                                        name="levelLockEnabled"
                                        control={control}
                                        defaultValue={false}
                                        render={({ field }) => (
                                            <Checkbox id="levelLockEnabled"
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        )}
                                    />
                                    <label htmlFor="levelLockEnabled" className="checkbox-label">Ratchet Enabled</label>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Trigger (%)</TableCell>
                                            <TableCell>Add (%)</TableCell>
                                            <TableCell>TSL (%)</TableCell>
                                            <TableCell>Cap</TableCell>
                                            <TableCell>Lock Target (%)</TableCell>
                                            <TableCell>Lock Floor (%)</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {levelLockConfigFields.map((levelLockConfigField, index) => (
                                            <TableRow key={levelLockConfigField.id}>
                                                <TableCell>
                                                    <Controller
                                                        name={`levelLockConfigs.${index}.trigger`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchLevelLockEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name={`levelLockConfigs.${index}.add`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchLevelLockEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name={`levelLockConfigs.${index}.tsl`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchLevelLockEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name={`levelLockConfigs.${index}.cap`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchLevelLockEnabled} />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name={`levelLockConfigs.${index}.lockTarget`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchLevelLockEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name={`levelLockConfigs.${index}.lockFloor`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField {...field} disabled={!watchLevelLockEnabled}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell style={{ whiteSpace: 'nowrap' }}>
                                                    <Tooltip title="Add row below">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={!watchLevelLockEnabled}
                                                                onClick={() => insertLevelLock(index + 1, { trigger: '', add: '', tsl: '', cap: '', lockTarget: '', lockFloor: '' })}
                                                                style={{ color: !watchLevelLockEnabled ? undefined : '#facc15' }}
                                                            >
                                                                <AddCircleOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={levelLockConfigFields.length <= 1 ? 'At least one config is required' : 'Delete row'}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={!watchLevelLockEnabled || levelLockConfigFields.length <= 1}
                                                                onClick={() => removeLevelLock(index)}
                                                                style={{ color: (!watchLevelLockEnabled || levelLockConfigFields.length <= 1) ? undefined : '#ef4444' }}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>

                        <div className="row" style={{ paddingLeft: '0.5rem' }}>
                            <div className="col-12 col-lg-6 col-xl-4 col-xxl-4">
                                <div className="form-control-wrapper">
                                    <label>Status</label>
                                    <Controller
                                        name="status"
                                        control={control}
                                        rules={{
                                            required: "Status is required"
                                        }}
                                        render={({ field }) => (
                                            <FormControl>
                                                <Select {...field}>
                                                    {statusTypes.map((status) => (
                                                        <MenuItem key={status.dataValue} value={status.dataValue} >
                                                            {status.displayValue}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="section-footer">
                            <Button style={{ marginLeft: '8px' }} type="submit" color="primary" variant="contained"
                                disableElevation>
                                Save Parameters
                            </Button>
                        </div>
                    </form>
                </div >
            </div >
        </>
    )
}

export default DynamicTradeManager; 